import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageCircle, X, Send, Bot, User } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PortalChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const endRef = useRef(null);

  const token = localStorage.getItem('hdh_portal_token');

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load history on first open
  useEffect(() => {
    if (open && messages.length === 0 && token) {
      axios.get(`${API}/api/homeowner/ai/history?session_id=${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => {
        if (r.data?.length > 0) {
          setMessages(r.data.map(m => ({ role: m.role, content: m.content })));
          setSessionId(r.data[0].session_id);
        }
      }).catch(() => {});
    }
  }, [open]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);

    try {
      const r = await axios.post(`${API}/api/homeowner/ai/chat`, {
        message: msg,
        session_id: sessionId,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(prev => [...prev, { role: 'assistant', content: r.data.message }]);
      if (r.data.session_id) setSessionId(r.data.session_id);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "I'm having trouble connecting. Please try again or contact your project manager." }]);
    }
    setLoading(false);
  };

  if (!token) return null;

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-[#0F1D32] hover:bg-[#1a2d45] text-white rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-105 z-50"
          data-testid="portal-chat-btn"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 w-[380px] h-[520px] bg-white rounded-xl shadow-2xl border border-slate-200 flex flex-col z-50 overflow-hidden" data-testid="portal-chat-panel">
          {/* Header */}
          <div className="bg-[#0F1D32] px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#F59E0B]/20 flex items-center justify-center">
                <Bot className="w-4 h-4 text-[#F59E0B]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">HDH Assistant</p>
                <p className="text-[10px] text-white/50">Ask about your pool project</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F8F6F3]">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <Bot className="w-10 h-10 text-[#0F1D32]/20 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-700">Hi! I'm your HDH assistant.</p>
                <p className="text-xs text-slate-400 mt-1">Ask me anything about your pool project.</p>
                <div className="flex flex-wrap gap-1.5 justify-center mt-4">
                  {['What stage is my pool at?', 'What equipment is included?', "What's my payment schedule?"].map(q => (
                    <button key={q} onClick={() => { setInput(q); }} className="text-[10px] px-2.5 py-1.5 bg-white border border-slate-200 rounded-full text-slate-600 hover:bg-[#0F1D32] hover:text-white transition-colors">
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {m.role === 'assistant' && (
                  <div className="w-6 h-6 rounded-full bg-[#0F1D32] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Bot className="w-3 h-3 text-[#F59E0B]" />
                  </div>
                )}
                <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
                  m.role === 'user'
                    ? 'bg-[#0F1D32] text-white'
                    : 'bg-white border border-slate-200 text-slate-700'
                }`}>
                  {m.content}
                </div>
                {m.role === 'user' && (
                  <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <User className="w-3 h-3 text-slate-500" />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-2">
                <div className="w-6 h-6 rounded-full bg-[#0F1D32] flex items-center justify-center flex-shrink-0">
                  <Bot className="w-3 h-3 text-[#F59E0B]" />
                </div>
                <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                    <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
                    <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
                  </div>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-slate-200 bg-white flex-shrink-0">
            <form onSubmit={e => { e.preventDefault(); handleSend(); }} className="flex gap-2">
              <Input
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Ask about your project..."
                className="flex-1 h-10 text-sm"
                disabled={loading}
                data-testid="portal-chat-input"
              />
              <Button type="submit" size="icon" className="h-10 w-10 bg-[#F59E0B] hover:bg-[#D97706] text-[#0F1D32]" disabled={!input.trim() || loading} data-testid="portal-chat-send">
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
